import { useCallback, useRef, useState } from 'react';
import { Button, CenterScreenModal } from '../atoms'
import { 
  useAddIncidentReportFormModalContext,
  useErrorHandlerContext,
  useIncidentDetailContext,
  useOpeningElementsControllerContext,
  useUserLanguage,
} from '@/customHooks'
import { Col, Row } from 'react-grid-system';
import { 
  FileOriginOption,
  FileTypeOption,
  useCreateIncidentReportFromSupplierMutation,
  useGetFileUploadUrlsMutation
} from '@/graphql/generated';
import { AddIncidentReportFormControlled, AddIncidentReportFormStateProps } from '../organism/AddIncidentReportFormControlled';
import textsData from '@/textData/AddIncidentReportFormTexts.json'

export const AddIncidentReportFormModal = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es.modal : textsData.en.modal

  const [isLoading, setIsLoading] = useState(false);
  const reportDataRef = useRef<AddIncidentReportFormStateProps | null>(null)
  const picturesRef = useRef<File[]>([])
  const videosRef = useRef<File[]>([])
  
  const [getUploadUrls] = useGetFileUploadUrlsMutation();
  const [createIncidentReport] = useCreateIncidentReportFromSupplierMutation();

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()

  const { reportData, isOpen, closeModal } = useAddIncidentReportFormModalContext()

  const { incident, setIncident } = useIncidentDetailContext()
  
  const { open: errorHandle } = useErrorHandlerContext()

  const onChangeHandle = (data: AddIncidentReportFormStateProps, pictures: File[], videos: File[]) => {
    reportDataRef.current = data
    picturesRef.current = pictures
    videosRef.current = videos
  }

  const onConfirmHandle = useCallback(async () => {
    if (!reportDataRef.current) return

    const onSuccesHandle = () => {
      confirmDialogModalOpen({
        title: texts.confirmation.create.title,
        description: texts.confirmation.create.description,
      });
      closeModal();
      setIncident(undefined);
    }

    try {
      let fileUploadsIds: string[] = [];
      setIsLoading(true);
      const pictures = picturesRef.current;
      const videos = videosRef.current;

      if (pictures.length > 0) {
        const getUploadUrlsResponse = await getUploadUrls({
          variables: {
            inputs: pictures.map((picture) => ({
              contentType: picture.type,
              fileExtension: 'webp',
              fileTypeOption: FileTypeOption.Picture,
              fileOriginOption: FileOriginOption.IncidentReport,
              fileSize: picture.size.toString(),
            })),
          },
        });

        const uploadDataList = getUploadUrlsResponse.data?.getUploadUrls;
        if (!uploadDataList || uploadDataList.length !== pictures.length) {
          throw new Error("No se pudieron obtener las URLs para subir las imágenes.");
        }

        const uploadPromises = uploadDataList.map((uploadData, index) => {
          if (!uploadData.uploadUrl) {
            throw new Error('URL de subida no disponible');
          }
          return fetch(uploadData.uploadUrl, {
            method: 'PUT',
            body: pictures[index],
            headers: { 'Content-Type': pictures[index].type },
          });
        });

        await Promise.all(uploadPromises);
        fileUploadsIds = uploadDataList.map(uploadData => uploadData.id);
      }

      if (videos.length > 0) {
        const getUploadUrlsResponse = await getUploadUrls({
          variables: {
            inputs: videos.map((video) => ({
              contentType: 'video/mp4',
              fileExtension: 'mp4',
              fileTypeOption: FileTypeOption.Video,
              fileOriginOption: FileOriginOption.IncidentReport,
              fileSize: video.size.toString(),
            })),
          },
        });

        const uploadDataList = getUploadUrlsResponse.data?.getUploadUrls;
        if (!uploadDataList || uploadDataList.length !== videos.length) {
          throw new Error("No se pudieron obtener las URLs para subir los videos.");
        }

        const uploadPromises = uploadDataList.map((uploadData, index) =>
          fetch(uploadData.uploadUrl!, {
            method: 'PUT',
            body: videos[index],
            headers: { 'Content-Type': 'video/mp4' },
          })
        );

        await Promise.all(uploadPromises);
        fileUploadsIds = fileUploadsIds.concat(uploadDataList.map(uploadData => uploadData.id));
      }

      await createIncidentReport({ variables: {
        incidentId: incident?.id ?? '',
        input: {
          subject: reportDataRef.current.subject,
          note: reportDataRef.current.note,
          fileUploadsIds,
        },
      }});
      onSuccesHandle();
    } catch (error: any) {
      errorHandle(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    createIncidentReport,
    getUploadUrls,
    incident?.id,
    setIncident,
    closeModal,
    confirmDialogModalOpen,
    errorHandle,
    texts.confirmation.create.title,
    texts.confirmation.create.description
  ]);
  
  return (
    
    <CenterScreenModal
    isOpen={isOpen}
    onClose={closeModal}
    >
      <Row>
        <Col>
          <h2>{texts.title}</h2>
        </Col>
      </Row>
      <AddIncidentReportFormControlled
        defaultReportData={reportData}
        onChange={onChangeHandle}
      />
      <br />
      <Row justify="center" gutterWidth={32}>
        <Col xs="content">
          <Button secondary onClick={() => closeModal()} disabled={isLoading}>
            {texts.cancelButton}
          </Button>
        </Col>
        <Col xs="content">
          <Button
            onClick={() => onConfirmHandle()}
            isLoading={isLoading}
          >
            {texts.confirmButton}
          </Button>
        </Col>
      </Row> 
    </CenterScreenModal>
  )
}
